import type { CmsPageTextColorMode } from "@/lib/cms/cms-theme.constants";
import type { CmsPageDto } from "@/lib/cms/cms-types";

export type CmsPageLocaleMeta = {
  pageBackgroundColorId: string | null;
  pageTextColorMode: CmsPageTextColorMode;
  pageTextColorId: string | null;
  pageFontFamily: string | null;
  pageFontVariant: string | null;
};

/** Forms with dynamic locale paths (e.g. `general.fr`). */
export type CmsFormDirtyChecker = {
  getFieldState(name: string): { isDirty?: boolean };
};

export function pageThemeFromDto<C>(page?: CmsPageDto<C>): CmsPageLocaleMeta {
  return {
    pageBackgroundColorId: page?.pageBackgroundColorId ?? null,
    pageTextColorMode: page?.pageTextColorMode ?? "AUTO",
    pageTextColorId: page?.pageTextColorId ?? null,
    pageFontFamily: page?.pageFontFamily ?? null,
    pageFontVariant: page?.pageFontVariant ?? null,
  };
}

/** @deprecated Use pageThemeFromDto */
export function pageBackgroundFromDto<C>(page?: CmsPageDto<C>): string | null {
  return page?.pageBackgroundColorId ?? null;
}

const PAGE_THEME_FIELD_NAMES = [
  "pageBackgroundColorId",
  "pageTextColorMode",
  "pageTextColorId",
  "pageFontFamily",
  "pageFontVariant",
] as const;

/** True when locale root or any page theme field was modified (drawer apparence). */
export function isCmsLocaleThemeDirty(
  form: CmsFormDirtyChecker,
  localePath: string,
): boolean {
  if (form.getFieldState(localePath).isDirty) return true;
  return PAGE_THEME_FIELD_NAMES.some((field) =>
    form.getFieldState(`${localePath}.${field}`).isDirty,
  );
}

/** Locale section dirty (content or page theme fields from the appearance drawer). */
export function isCmsLocaleSectionDirty(
  form: CmsFormDirtyChecker,
  localePath: string,
): boolean {
  return (
    form.getFieldState(localePath).isDirty || isCmsLocaleThemeDirty(form, localePath)
  );
}

export function isAnyCmsLocaleThemeDirty(
  form: CmsFormDirtyChecker,
  formPrefix: string,
  languages: readonly string[],
): boolean {
  return languages.some((lang) =>
    isCmsLocaleThemeDirty(form, `${formPrefix}.${lang}`),
  );
}

export function buildCmsPagePatchPayload<C extends object>({
  pageName,
  published,
  pageBackgroundColorId,
  pageTextColorMode,
  pageTextColorId,
  pageFontFamily,
  pageFontVariant,
  content,
}: {
  pageName: string;
  published: boolean;
  pageBackgroundColorId?: string | null;
  pageTextColorMode?: CmsPageTextColorMode;
  pageTextColorId?: string | null;
  pageFontFamily?: string | null;
  pageFontVariant?: string | null;
  content: C;
}) {
  const {
    pageBackgroundColorId: contentBackgroundColorId,
    pageTextColorMode: contentTextColorMode,
    pageTextColorId: contentTextColorId,
    pageFontFamily: contentFontFamily,
    pageFontVariant: contentFontVariant,
    ...contentWithoutTheme
  } = content as C & Partial<CmsPageLocaleMeta>;
  const mode = pageTextColorMode ?? contentTextColorMode ?? "AUTO";

  return {
    pageName,
    published,
    pageBackgroundColorId: pageBackgroundColorId ?? contentBackgroundColorId ?? null,
    pageTextColorMode: mode,
    pageTextColorId:
      mode === "CUSTOM" ? (pageTextColorId ?? contentTextColorId ?? null) : null,
    pageFontFamily: pageFontFamily ?? contentFontFamily ?? null,
    pageFontVariant: pageFontVariant ?? contentFontVariant ?? null,
    content: contentWithoutTheme as Omit<C, keyof CmsPageLocaleMeta>,
  };
}
